/**
 * Member Portal - Work Compliance Page
 * Displays work requirement compliance status for Medicaid H.R.1 requirements
 * Members can view their compliance status, submit hours, and upload documents
 */

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { getMemberData, getMemberToken, isAuthenticated, logoutMember } from '@/lib/memberAuth';
import { getWeaveinApiUrl } from '@/src/config';
import { PageContainer, PageHeader, PageState } from '@/components/weavein';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  ArrowLeft,
  AlertCircle,
  CheckCircle,
  Clock,
  Calendar,
  Upload,
  FileText,
  DollarSign,
  Star,
  TrendingUp,
  Award,
  Briefcase,
  Target,
  ClipboardList,
} from 'lucide-react';

interface WorkflowStep {
  step_name: string;
  step_code: string;
  step_type: string;
  description: string;
  step_order: number;
  is_optional: boolean;
  is_critical: boolean;
  status: 'pending' | 'in_progress' | 'completed' | 'skipped' | 'failed';
  started_date: string | null;
  completed_date: string | null;
  completion_notes: string | null;
}

interface WorkflowProgress {
  workflow_instance_id: number;
  template_name: string;
  template_description: string;
  status: string;
  progress_percentage: number;
  completed_steps: number;
  total_steps: number;
  start_date: string | null;
  target_completion_date: string | null;
  steps: WorkflowStep[];
}

interface ComplianceData {
  enrollment_id: number;
  member_id: number;
  current_month: number;
  current_year: number;
  current_month_hours: number;
  current_month_compliant: boolean;
  current_month_status: string;
  deadline_date: string;
  days_until_deadline: number;
  hours_needed: number;
  last_6_months_compliance: Array<{
    month: number;
    year: number;
    total_hours: number;
    compliant: boolean;
    verification_status: string;
    submitted_date?: string;
  }>;
  consecutive_compliant_months: number;
  active_alerts: Array<{
    id: number;
    type: string;
    severity: string;
    title: string;
    message: string;
    deadline_date?: string;
  }>;
  pending_verifications: number;
  total_points_earned: number;
  total_incentives_earned: number;
  total_incentives_paid: number;
  incentives_pending: number;
}

export default function MemberWorkCompliancePage() {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState<ComplianceData | null>(null);
  const [workflowProgress, setWorkflowProgress] = useState<WorkflowProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [noEnrollment, setNoEnrollment] = useState(false);
  const [showSubmitHours, setShowSubmitHours] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    employment_hours: 0,
    education_hours: 0,
    volunteer_hours: 0,
    job_training_hours: 0,
    job_search_hours: 0,
    community_service_hours: 0,
    employer_name: '',
    education_institution: '',
    volunteer_organization: '',
    training_program_name: '',
    notes: '',
  });

  const member = getMemberData();

  useEffect(() => {
    // Check authentication
    const checkAuthAndLoadData = async () => {
      try {
        // Check if user is logged in as organization/staff instead of member
        const orgToken = localStorage.getItem('auth_token');
        if (orgToken && !member) {
          alert('This page is for members only. Redirecting to organization dashboard.');
          navigate('/organizations', { replace: true });
          return;
        }

        if (!isAuthenticated()) {
          navigate('/member/login', { replace: true });
          return;
        }

        if (!member) {
          logoutMember();
          navigate('/member/login', { replace: true });
          return;
        }

        // Load compliance data
        await loadComplianceData();
      } catch (err: any) {
        if (err.message?.includes('Not authenticated') || err.message?.includes('Unauthorized')) {
          logoutMember();
          navigate('/member/login', { replace: true });
          return;
        }
        setError(err.message || 'Failed to load compliance data');
      } finally {
        setLoading(false);
      }
    };

    checkAuthAndLoadData();
  }, [navigate]);

  const loadComplianceData = async () => {
    if (!member?.id) return;

    const token = getMemberToken();
    if (!token) {
      setError('Authentication token not found');
      return;
    }

    try {
      const url = getWeaveinApiUrl(`/api/work-compliance/member/${member.id}/dashboard`);
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 404) {
        setNoEnrollment(true);
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to load compliance data: ${response.status}`);
      }

      const result = await response.json();
      setData(result);

      // Also load workflow progress
      try {
        const wfUrl = getWeaveinApiUrl(`/api/work-compliance/member/${member.id}/workflow-progress`);
        const wfResponse = await fetch(wfUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
            'Content-Type': 'application/json'
          }
        });
        if (wfResponse.ok) {
          const wfResult = await wfResponse.json();
          setWorkflowProgress(wfResult);
        }
      } catch (wfErr) {
        console.log('Workflow progress not available:', wfErr);
      }
    } catch (err: any) {
      console.error('Error fetching compliance data:', err);
      setError(err.message || 'Failed to load compliance data');
    }
  };

  const handleSubmitHours = async () => {
    if (!data) return;
    setSubmitting(true);
    setSubmitError('');
    setSubmitSuccess('');

    const totalHours =
      formData.employment_hours +
      formData.education_hours +
      formData.volunteer_hours +
      formData.job_training_hours +
      formData.job_search_hours +
      formData.community_service_hours;

    if (totalHours <= 0) {
      setSubmitError('Please enter at least some hours.');
      setSubmitting(false);
      return;
    }

    try {
      const token = getMemberToken();
      const url = getWeaveinApiUrl('/api/work-compliance/submit-work-activity');
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          enrollment_id: data.enrollment_id,
          month: data.current_month,
          year: data.current_year,
          ...formData,
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.detail || `Submission failed: ${response.status}`);
      }

      const result = await response.json();
      setSubmitSuccess(
        `Successfully submitted ${result.total_hours} hours for ${getMonthName(data.current_month)} ${data.current_year}. Your submission is pending verification.`
      );
      setFormData({
        employment_hours: 0,
        education_hours: 0,
        volunteer_hours: 0,
        job_training_hours: 0,
        job_search_hours: 0,
        community_service_hours: 0,
        employer_name: '',
        education_institution: '',
        volunteer_organization: '',
        training_program_name: '',
        notes: '',
      });

      // Refresh dashboard data
      await loadComplianceData();

      // Auto-close form after a short delay
      setTimeout(() => {
        setShowSubmitHours(false);
        setSubmitSuccess('');
      }, 3000);
    } catch (err: any) {
      setSubmitError(err.message || 'Failed to submit hours');
    } finally {
      setSubmitting(false);
    }
  };

  const formTotalHours =
    formData.employment_hours +
    formData.education_hours +
    formData.volunteer_hours +
    formData.job_training_hours +
    formData.job_search_hours +
    formData.community_service_hours;

  const getMonthName = (month: number) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[month - 1];
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      VERIFIED: {
        label: 'Verified',
        className: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800',
      },
      APPROVED: {
        label: 'Approved',
        className: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800',
      },
      PENDING: {
        label: 'Pending',
        className: 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-800',
      },
      REJECTED: {
        label: 'Rejected',
        className: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800',
      },
      NOT_SUBMITTED: {
        label: 'Not Submitted',
        className: 'bg-paper text-ink-700 border-v2border dark:bg-gray-900/30 dark:text-ink-300 dark:border-gray-800',
      },
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    return (
      <Badge variant="outline" className={config.className}>
        {config.label}
      </Badge>
    );
  };

  const header = (
    <PageHeader
      leading={
        <Link to="/member/dashboard">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('work-compliance.back_1', 'Back')}
          </Button>
        </Link>
      }
      title={t('work-compliance.work_requirements_1', 'Work Requirements')}
      description={t('work-compliance.medicaid_compliance_tracking', 'Medicaid Compliance Tracking')}
    />
  );

  if (loading) {
    return (
      <>
        {header}
        <PageContainer size="wide">
          <PageState variant="loading" />
        </PageContainer>
      </>
    );
  }

  // No Medicaid enrollment with work requirements
  if (noEnrollment) {
    return (
      <>
        {header}
        <PageContainer size="wide">
          <Card>
            <CardContent className="py-12 text-center">
              <Briefcase className="w-12 h-12 text-ink-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-ink mb-2">
                {t('work-compliance.no_work_requirement_enrollment', 'No Work Requirement Enrollment')}
              </h3>
              <p className="text-ink-500 max-w-md mx-auto">
                You are not currently enrolled in a Medicaid program with work requirements.
                If you believe this is an error, please contact your case manager.
              </p>
              <div className="mt-6">
                <Link to="/member/dashboard">
                  <Button>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t('work-compliance.return_to_dashboard', 'Return to Dashboard')}
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </PageContainer>
      </>
    );
  }

  const progressPercentage = data ? Math.min((data.current_month_hours / 80) * 100, 100) : 0;

  return (
    <>
      {header}
      <PageContainer size="wide">
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {data && (
          <div className="space-y-6">
            {/* Active Alerts */}
            {data.active_alerts.length > 0 && (
              <Alert className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30">
                <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <AlertDescription className="dark:text-gray-200">
                  {data.active_alerts.map(alert => (
                    <div key={alert.id} className="mb-2 last:mb-0">
                      <strong>{alert.title}</strong>: {alert.message}
                    </div>
                  ))}
                </AlertDescription>
              </Alert>
            )}

            {/* Current Month Progress */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Progress Card */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-xl">
                        {getMonthName(data.current_month)} {data.current_year}
                      </CardTitle>
                      <CardDescription>{t('work-compliance.monthly_work_hours_requirement', 'Monthly Work Hours Requirement')}</CardDescription>
                    </div>
                    <div className={`flex items-center gap-1 ${
                      data.days_until_deadline <= 7 ? 'text-red-600 dark:text-red-400' : 'text-ink-500'
                    }`}>
                      <Clock className="w-4 h-4" />
                      <span className="text-sm font-medium">{data.days_until_deadline} days left</span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-ink-500">{t('work-compliance.hours_submitted', 'Hours Submitted')}</span>
                    <span className="font-bold text-ink">
                      {data.current_month_hours} / 80
                    </span>
                  </div>

                  <Progress value={progressPercentage} className="h-3" />

                  <div className="flex items-center justify-between pt-2">
                    {getStatusBadge(data.current_month_status)}

                    {data.current_month_compliant ? (
                      <div className="flex items-center text-green-600 dark:text-green-400 gap-1">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-semibold">{t('work-compliance.compliant', 'Compliant')}</span>
                      </div>
                    ) : (
                      <div className="flex items-center text-amber-600 dark:text-amber-400 gap-1">
                        <Target className="w-5 h-5" />
                        <span className="font-semibold">{data.hours_needed} hours needed</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Incentives Card */}
              <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800">
                <CardHeader>
                  <div className="flex items-center space-x-2">
                    <Award className="w-5 h-5 text-yellow-500" />
                    <CardTitle className="text-xl text-green-900 dark:text-green-100">
                      {t('work-compliance.earned_incentives', 'Earned Incentives')}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Star className="text-yellow-500 w-5 h-5" />
                      <span className="text-green-700 dark:text-green-300">{t('work-compliance.points_earned', 'Points Earned')}</span>
                    </div>
                    <span className="font-display text-2xl font-semibold text-green-900 dark:text-green-100">
                      {data.total_points_earned}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <DollarSign className="text-green-500 w-5 h-5" />
                      <span className="text-green-700 dark:text-green-300">{t('work-compliance.total_earned', 'Total Earned')}</span>
                    </div>
                    <span className="font-display text-2xl font-semibold text-green-900 dark:text-green-100">
                      ${data.total_incentives_earned.toFixed(2)}
                    </span>
                  </div>

                  <div className="border-t border-green-200 dark:border-green-700 pt-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-green-700 dark:text-green-300">{t('work-compliance.paid', 'Paid')}</span>
                      <span className="font-semibold text-green-900 dark:text-green-100">
                        ${data.total_incentives_paid.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-green-700 dark:text-green-300">{t('work-compliance.pending', 'Pending')}</span>
                      <span className="font-semibold text-green-900 dark:text-green-100">
                        ${data.incentives_pending.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Streak Card */}
            {data.consecutive_compliant_months > 0 && (
              <Card className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-blue-200 dark:border-blue-800">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <TrendingUp className="text-blue-600 dark:text-blue-400 w-6 h-6" />
                      <div>
                        <p className="font-semibold text-lg text-blue-900 dark:text-blue-100">
                          {data.consecutive_compliant_months} Month Streak!
                        </p>
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          {t('work-compliance.keep_it_up_youre_on', 'Keep it up! You\'re on track for quarterly/annual bonuses.')}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 6-Month History */}
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <Calendar className="w-5 h-5 text-ink-500" />
                  <CardTitle>6-Month Compliance History</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                  {data.last_6_months_compliance.map((month, index) => (
                    <div
                      key={index}
                      className={`p-3 rounded-lg border text-center ${
                        month.compliant
                          ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800'
                          : month.verification_status === 'PENDING'
                          ? 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800'
                          : 'bg-paper border-v2border'
                      }`}
                    >
                      <p className="text-xs font-semibold text-ink-500">
                        {getMonthName(month.month)} '{month.year.toString().slice(-2)}
                      </p>
                      <p className="font-display text-2xl font-semibold mt-1 text-ink">
                        {month.total_hours}
                      </p>
                      <p className="text-xs text-ink-500">hrs</p>
                      {month.compliant ? (
                        <CheckCircle className="mx-auto mt-2 text-green-600 dark:text-green-400 w-4 h-4" />
                      ) : (
                        <Badge variant="outline" className="text-xs mt-2">
                          {month.verification_status?.replace('_', ' ') || 'N/A'}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Pending Verifications Alert */}
            {data.pending_verifications > 0 && (
              <Alert className="border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/30">
                <FileText className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                <AlertDescription className="dark:text-gray-200">
                  <strong>{data.pending_verifications} submission(s)</strong> pending verification by your CBO.
                  You'll be notified when complete (typically within 3 business days).
                </AlertDescription>
              </Alert>
            )}

            {/* Enrollment Workflow Progress */}
            {workflowProgress && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <ClipboardList className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      <CardTitle>Enrollment Workflow</CardTitle>
                    </div>
                    <Badge variant="outline" className={
                      workflowProgress.status === 'completed'
                        ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300'
                    }>
                      {workflowProgress.completed_steps} / {workflowProgress.total_steps} steps
                    </Badge>
                  </div>
                  <CardDescription>{workflowProgress.template_name}</CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Progress bar */}
                  <Progress value={workflowProgress.progress_percentage} className="h-2 mb-4" />

                  {/* Steps list */}
                  <div className="space-y-2">
                    {workflowProgress.steps.map((step, index) => (
                      <div
                        key={step.step_code}
                        className={`flex items-start gap-3 p-3 rounded-lg border ${
                          step.status === 'completed'
                            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                            : step.status === 'in_progress'
                            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                            : step.status === 'skipped'
                            ? 'bg-paper border-v2border opacity-60'
                            : 'bg-surface border-v2border'
                        }`}
                      >
                        {/* Step number / icon */}
                        <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                          step.status === 'completed'
                            ? 'bg-green-500 text-white'
                            : step.status === 'in_progress'
                            ? 'bg-blue-500 text-white'
                            : 'bg-paper-2 dark:bg-gray-700 text-ink-500'
                        }`}>
                          {step.status === 'completed' ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            index + 1
                          )}
                        </div>

                        {/* Step info */}
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${
                            step.status === 'completed'
                              ? 'text-green-800 dark:text-green-200'
                              : step.status === 'in_progress'
                              ? 'text-blue-800 dark:text-blue-200'
                              : 'text-ink-500'
                          }`}>
                            {step.step_name}
                            {step.is_optional && (
                              <span className="text-xs font-normal text-ink-400 ml-2">(optional)</span>
                            )}
                          </p>
                          {step.status === 'in_progress' && step.description && (
                            <p className="text-xs text-ink-500 mt-0.5 line-clamp-2">
                              {step.description}
                            </p>
                          )}
                          {step.completed_date && (
                            <p className="text-xs text-ink-400 mt-0.5">
                              Completed {new Date(step.completed_date).toLocaleDateString()}
                            </p>
                          )}
                        </div>

                        {/* Status badge */}
                        <div className="flex-shrink-0">
                          {step.status === 'in_progress' && (
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300">
                              Current
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {workflowProgress.target_completion_date && (
                    <p className="text-xs text-ink-500 mt-4 text-center">
                      Target completion: {new Date(workflowProgress.target_completion_date).toLocaleDateString()}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Submit Work Hours Form */}
            {showSubmitHours && (
              <Card className="border-2 border-blue-300 dark:border-blue-700">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Upload className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      <CardTitle>
                        Submit Work Hours - {getMonthName(data.current_month)} {data.current_year}
                      </CardTitle>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowSubmitHours(false);
                        setSubmitError('');
                        setSubmitSuccess('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                  <CardDescription>
                    Enter your work hours by category. You need at least 80 hours total to be compliant.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {submitSuccess && (
                    <Alert className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/30">
                      <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                      <AlertDescription className="text-green-800 dark:text-green-200">{submitSuccess}</AlertDescription>
                    </Alert>
                  )}

                  {submitError && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{submitError}</AlertDescription>
                    </Alert>
                  )}

                  {/* Hours Breakdown */}
                  <div>
                    <h4 className="text-sm font-semibold text-ink-700 mb-3">Hours by Category</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {[
                        { key: 'employment_hours', label: 'Employment', icon: Briefcase },
                        { key: 'education_hours', label: 'Education', icon: FileText },
                        { key: 'volunteer_hours', label: 'Volunteer', icon: Star },
                        { key: 'job_training_hours', label: 'Job Training', icon: Target },
                        { key: 'job_search_hours', label: 'Job Search', icon: Clock },
                        { key: 'community_service_hours', label: 'Community Service', icon: Award },
                      ].map(({ key, label, icon: Icon }) => (
                        <div key={key} className="space-y-1">
                          <label className="flex items-center gap-1.5 text-sm font-medium text-ink-500">
                            <Icon className="w-3.5 h-3.5" />
                            {label}
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="744"
                            step="0.5"
                            value={formData[key as keyof typeof formData] || ''}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                [key]: parseFloat(e.target.value) || 0,
                              }))
                            }
                            className="w-full px-3 py-2 border border-v2border rounded-lg bg-surface text-ink focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="0"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Total */}
                  <div className={`flex items-center justify-between p-3 rounded-lg border ${
                    formTotalHours >= 80
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                      : formTotalHours > 0
                      ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                      : 'bg-paper border-v2border'
                  }`}>
                    <span className="font-semibold text-ink-700">Total Hours</span>
                    <div className="flex items-center gap-2">
                      <span className="font-display text-2xl font-semibold text-ink">{formTotalHours}</span>
                      <span className="text-sm text-ink-500">/ 80 required</span>
                      {formTotalHours >= 80 && <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />}
                    </div>
                  </div>

                  {/* Optional Details */}
                  <div>
                    <h4 className="text-sm font-semibold text-ink-700 mb-3">Details (Optional)</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {formData.employment_hours > 0 && (
                        <div className="space-y-1">
                          <label className="text-sm font-medium text-ink-500">Employer Name</label>
                          <input
                            type="text"
                            value={formData.employer_name}
                            onChange={(e) => setFormData((prev) => ({ ...prev, employer_name: e.target.value }))}
                            className="w-full px-3 py-2 border border-v2border rounded-lg bg-surface text-ink focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="e.g. Acme Corp"
                          />
                        </div>
                      )}
                      {formData.education_hours > 0 && (
                        <div className="space-y-1">
                          <label className="text-sm font-medium text-ink-500">Education Institution</label>
                          <input
                            type="text"
                            value={formData.education_institution}
                            onChange={(e) => setFormData((prev) => ({ ...prev, education_institution: e.target.value }))}
                            className="w-full px-3 py-2 border border-v2border rounded-lg bg-surface text-ink focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="e.g. Community College"
                          />
                        </div>
                      )}
                      {formData.volunteer_hours > 0 && (
                        <div className="space-y-1">
                          <label className="text-sm font-medium text-ink-500">Volunteer Organization</label>
                          <input
                            type="text"
                            value={formData.volunteer_organization}
                            onChange={(e) => setFormData((prev) => ({ ...prev, volunteer_organization: e.target.value }))}
                            className="w-full px-3 py-2 border border-v2border rounded-lg bg-surface text-ink focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="e.g. Red Cross"
                          />
                        </div>
                      )}
                      {formData.job_training_hours > 0 && (
                        <div className="space-y-1">
                          <label className="text-sm font-medium text-ink-500">Training Program</label>
                          <input
                            type="text"
                            value={formData.training_program_name}
                            onChange={(e) => setFormData((prev) => ({ ...prev, training_program_name: e.target.value }))}
                            className="w-full px-3 py-2 border border-v2border rounded-lg bg-surface text-ink focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="e.g. Workforce Development"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Notes */}
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-ink-500">Notes (Optional)</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                      rows={2}
                      className="w-full px-3 py-2 border border-v2border rounded-lg bg-surface text-ink focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Any additional notes about your work activity..."
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end gap-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowSubmitHours(false);
                        setSubmitError('');
                        setSubmitSuccess('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSubmitHours}
                      disabled={submitting || formTotalHours <= 0}
                      className="bg-v2accent hover:bg-v2accent/90 text-white"
                    >
                      {submitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Submit {formTotalHours > 0 ? `${formTotalHours} Hours` : 'Hours'}
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>{t('work-compliance.quick_actions', 'Quick Actions')}</CardTitle>
                <CardDescription>{t('work-compliance.submit_hours_and_documents_for', 'Submit hours and documents for work requirement compliance')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card
                    className="border-2 border-blue-200 dark:border-blue-800 hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => setShowSubmitHours(true)}
                  >
                    <CardContent className="p-4">
                      <Upload className="text-blue-600 dark:text-blue-400 mb-2 w-6 h-6" />
                      <p className="font-semibold text-ink">{t('work-compliance.submit_work_hours', 'Submit Work Hours')}</p>
                      <p className="text-sm text-ink-500">{t('work-compliance.for_current_month', 'For current month')}</p>
                    </CardContent>
                  </Card>

                  <Card
                    className="border-2 border-purple-200 dark:border-purple-800 hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate('/member/documents')}
                  >
                    <CardContent className="p-4">
                      <FileText className="text-purple-600 dark:text-purple-400 mb-2 w-6 h-6" />
                      <p className="font-semibold text-ink">{t('work-compliance.upload_documents', 'Upload Documents')}</p>
                      <p className="text-sm text-ink-500">{t('work-compliance.pay_stubs_timesheets', 'Pay stubs, timesheets')}</p>
                    </CardContent>
                  </Card>

                  <Card
                    className="border-2 border-green-200 dark:border-green-800 hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate('/member/enrollments')}
                  >
                    <CardContent className="p-4">
                      <Calendar className="text-green-600 dark:text-green-400 mb-2 w-6 h-6" />
                      <p className="font-semibold text-ink">{t('work-compliance.view_full_history', 'View Full History')}</p>
                      <p className="text-sm text-ink-500">{t('work-compliance.all_submissions', 'All submissions')}</p>
                    </CardContent>
                  </Card>
                </div>
                {/* WeaveIn Verify: the line-by-line record organizations filed, with dispute. */}
                <Link
                  to="/member/verified-hours"
                  className="mt-4 inline-flex items-center text-sm font-medium text-v2accent-ink hover:underline"
                >
                  {t('verify.member.link', 'See every verified hour, who filed it, and dispute a line')}
                </Link>
              </CardContent>
            </Card>

            {/* Help Section */}
            <Card className="bg-paper/50">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-teal-600 dark:text-teal-400 w-5 h-5 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-ink">
                      {t('work-compliance.need_help', 'Need Help?')}
                    </p>
                    <p className="text-sm text-ink-500">
                      If you have questions about work requirements or need assistance submitting your hours,
                      please contact your case manager through the{' '}
                      <Link to="/member/inbox" className="text-teal-600 dark:text-teal-400 hover:underline">
                        {t('work-compliance.messages', 'Messages')}
                      </Link>{' '}
                      section.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </PageContainer>
    </>
  );
}
